// The transaction ids staff paste or upload on Admin → Reversals. Read in the browser (no file
// leaves it) and checked again by the API with the same rules, so the list staff preview is the
// list that is applied. Plain text only: nothing here touches the database.

/** Most transaction ids one reversal batch may carry. */
export const MAX_REVERSAL_IDS = 1000;
/** A postback keeps the first 200 characters of a transaction id (parsePostback); so do we. */
export const MAX_TXID_LENGTH = 200;

export interface IdList {
  /** Unique ids in the order first seen. */
  ids: string[];
  /** Repeats that were dropped. */
  duplicates: number;
  /** Values cut to MAX_TXID_LENGTH characters, as a postback stores them. */
  truncated: number;
}

/**
 * Trims each value, removes quotes around it, drops blanks and repeats (the first one stays), and
 * cuts it to 200 characters the way a postback's id is stored. Ids are compared exactly:
 * networks' ids are case-sensitive.
 */
export function uniqueIds(values: Iterable<string>): IdList {
  const seen = new Set<string>();
  let duplicates = 0;
  let truncated = 0;
  for (const raw of values) {
    let v = raw.trim();
    const q = /^(["'])(.*)\1$/s.exec(v);
    if (q) v = q[2]!.trim();
    if (!v) continue;
    if (v.length > MAX_TXID_LENGTH) {
      v = v.slice(0, MAX_TXID_LENGTH);
      truncated++;
    }
    if (seen.has(v)) duplicates++;
    else seen.add(v);
  }
  return { ids: [...seen], duplicates, truncated };
}

/** Pasted ids: one per line, or separated by commas, semicolons, tabs or spaces. */
export function parseIdText(text: string): IdList {
  return uniqueIds(text.split(/[\s,;]+/));
}

/**
 * Whether pasted text is a table rather than a list of ids, so staff pick its id column instead
 * of every cell becoming an id: anything with a tab (a copy from a spreadsheet or a web page), or
 * several lines that mostly have the same number (2 or more) of comma or semicolon separated cells.
 */
export function looksLikeTable(text: string): boolean {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.some((l) => l.includes('\t'))) return true;
  if (lines.length < 2) return false;
  const rows = parseDelimited(lines.join('\n'));
  const counts = new Map<number, number>();
  for (const r of rows) counts.set(r.length, (counts.get(r.length) ?? 0) + 1);
  const [width, n] = [...counts].sort((a, b) => b[1] - a[1])[0] ?? [0, 0];
  return width >= 2 && n >= rows.length * 0.8;
}

/** Why a list can't be applied as one batch, in words for staff; null when it can. */
export function idListError(count: number): string | null {
  if (count === 0) return 'Add at least one transaction id.';
  if (count > MAX_REVERSAL_IDS)
    return `${count.toLocaleString('en-US')} transaction ids is more than one batch can take (${MAX_REVERSAL_IDS.toLocaleString('en-US')}). Split the list and run it in parts.`;
  return null;
}

/**
 * CSV or TSV text as rows of cells. The delimiter is whichever of comma, semicolon, tab or pipe
 * the first line uses most (outside quotes). Quoted cells may hold delimiters, line breaks and
 * doubled quotes. Rows with nothing in them are left out.
 */
export function parseDelimited(text: string): string[][] {
  const s = text.replace(/^\uFEFF/, '');
  const delim = guessDelimiter(s);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const endRow = () => {
    row.push(cell);
    if (row.some((c) => c.trim() !== '')) rows.push(row);
    row = [];
    cell = '';
  };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!;
    if (quoted) {
      if (ch === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"' && cell.trim() === '') {
      cell = '';
      quoted = true;
    } else if (ch === delim) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      endRow();
    } else cell += ch;
  }
  if (cell !== '' || row.length) endRow();
  return rows;
}

function guessDelimiter(s: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0, '|': 0 };
  let quoted = false;
  for (const ch of s) {
    if (ch === '"') quoted = !quoted;
    else if (!quoted && (ch === '\n' || ch === '\r')) break;
    else if (!quoted && ch in counts) counts[ch]!++;
  }
  let best = ',';
  for (const [d, n] of Object.entries(counts)) if (n > counts[best]!) best = d;
  return best;
}

/** A header as lower-case words: "TransID", "trans_id" and "Trans ID" all read "trans id". */
const words = (h: string) =>
  h
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[\s_.\-#:/]+/g, ' ')
    .toLowerCase()
    .trim();

/** The network's own transaction id, by its header. */
const TXID_HEADER = /^(transaction|trans|txn|tx|txid)( (id|ref|reference|no|number|nr))?$/;
/** Headers that mention the transaction and say more ("network transaction id"). */
const TXID_MENTION = /\b(transaction|trans|txn|txid)\b/;
/** Other ids a network may report a lead under. */
const LEAD_HEADER =
  /^(conversion|lead|click|order|sale|reference|ref)( (id|ref|reference|no|number|nr))?$/;
/** Columns never picked by themselves: people, offers, money, dates, statuses. */
const NOT_ID =
  /\b(user|users|member|player|sub|subid|uid|offer|offers|campaign|app|payout|amount|revenue|reward|points|coins|currency|price|status|state|reason|date|time|timestamp|ip|country|geo|device|name|email|event|type|description)\b/;
const HEADER_WORD = /\b(id|ids|ref|reference|number)\b/;

/**
 * Which column most likely holds the transaction ids, and whether the first row is a header. A
 * header naming the transaction id wins; then one that mentions it; then a conversion, lead,
 * click or order id. Columns about users, offers, amounts, dates or statuses are never picked by
 * themselves. Without a usable header, the column with the most distinct id-like values (letters
 * and digits, 4 characters or more) is the guess; null means staff must choose.
 */
export function guessIdColumn(rows: string[][]): { column: number | null; header: boolean } {
  const first = (rows[0] ?? []).map(words);
  for (const test of [
    (h: string) => TXID_HEADER.test(h),
    (h: string) => TXID_MENTION.test(h) && !NOT_ID.test(h),
    (h: string) => LEAD_HEADER.test(h),
  ]) {
    const col = first.findIndex(test);
    if (col >= 0) return { column: col, header: true };
  }
  const header = first.some((h) => NOT_ID.test(h) || HEADER_WORD.test(h));
  const sample = rows.slice(0, 201);
  const body = sample.slice(header ? 1 : 0);
  const width = Math.max(0, ...sample.map((r) => r.length));
  let best: { column: number | null; score: number } = { column: null, score: 0 };
  for (let col = 0; col < width; col++) {
    if (header && NOT_ID.test(first[col] ?? '')) continue;
    const idLike = body
      .map((r) => (r[col] ?? '').trim())
      .filter((v) => /^[\w.:-]{4,}$/.test(v) && /\d/.test(v));
    const score = new Set(idLike).size;
    if (score > best.score) best = { column: col, score };
  }
  return { column: best.column, header };
}

/** The ids in one column (after the header row, when there is one). */
export function columnIds(rows: string[][], column: number, header: boolean): IdList {
  return uniqueIds(rows.slice(header ? 1 : 0).map((r) => r[column] ?? ''));
}

/** "A", "B" … "Z", "AA" for a 0-based column, as spreadsheets label them. */
export function columnLetter(i: number): string {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26))
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}
